import { FC, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEndpoints } from '../../../hooks/useEndpoints';
import dayjs from 'dayjs';
import styles from './AthleteBodyMapHistoryView.module.scss';
import {
  ZONE_LABELS,
  TYPE_LABELS,
  IMPACT_LABELS,
  getIntensityColor,
  getIntensityLabel,
  getImpactColor,
} from '../../../shared/bodyMap/bodyMapScale';
import { DistributionSlice } from '../../../shared/charts/chart.types';
import DistributionDonut from '../../../shared/charts/DistributionDonut';
import KpiCard from '../../../shared/KpiCard/KpiCard';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { getPaletteColor } from '../AthleteInjuryCharts/charts/_shared/chartPalette';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ComplaintItem {
  zone: string;
  intensity: number;
  type: 'pain' | 'stiffness' | 'swelling' | 'weakness';
  impact: 'can_do_all' | 'will_limit' | 'cannot_do_all';
  note?: string;
}

interface BodyMapDocument {
  _id: string;
  userId: string;
  date: string;
  complaints: ComplaintItem[];
}

// ─── Main ─────────────────────────────────────────────────────────────────────

interface Props {
  athleteId: string;
}

const AthleteBodyMapHistoryView: FC<Props> = ({ athleteId }) => {
  const endpoints = useEndpoints();

  const today = dayjs().format('YYYY-MM-DD');
  // Range di default ampio (12 mesi): un default troppo stretto (90gg) rischiava
  // di non includere segnalazioni reali più vecchie, mostrando un falso "vuoto".
  const defaultFrom = dayjs().subtract(365, 'day').format('YYYY-MM-DD');

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(today);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  const { data: docs, isLoading, isError, refetch } = useQuery<BodyMapDocument[]>({
    queryKey: ['bodymap-history', athleteId, dateFrom, dateTo],
    queryFn: async () => {
      const { data } = await endpoints.getBodyMapAthlete(athleteId, {
        startDate: dateFrom,
        endDate: dateTo,
        limit: 500,
      });
      return data as BodyMapDocument[];
    },
    enabled: !!athleteId,
    staleTime: 1000 * 60 * 3,
  });

  // ── Flatten all complaints from all docs ────────────────────────────────────
  const allComplaints = useMemo(() => {
    if (!docs) return [];
    return docs.flatMap((doc) =>
      doc.complaints.map((c) => ({ ...c, date: doc.date, docId: doc._id }))
    );
  }, [docs]);

  // ── Stats: zone distribution ────────────────────────────────────────────────
  const zoneStats = useMemo(() => {
    const map = new Map<string, { count: number; totalIntensity: number }>();
    for (const c of allComplaints) {
      const existing = map.get(c.zone) ?? { count: 0, totalIntensity: 0 };
      map.set(c.zone, {
        count: existing.count + 1,
        totalIntensity: existing.totalIntensity + c.intensity,
      });
    }
    return Array.from(map.entries())
      .map(([zone, { count, totalIntensity }]) => ({
        zone,
        label: ZONE_LABELS[zone] ?? zone,
        count,
        avgIntensity: Math.round((totalIntensity / count) * 10) / 10,
      }))
      .sort((a, b) => b.count - a.count);
  }, [allComplaints]);

  // ── Stats: type distribution ────────────────────────────────────────────────
  const typeStats = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of allComplaints) {
      map.set(c.type, (map.get(c.type) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .map(([type, count]) => ({ type, label: TYPE_LABELS[type] ?? type, count }))
      .sort((a, b) => b.count - a.count);
  }, [allComplaints]);

  const totalComplaints = allComplaints.length;

  // ── Dati donut (DistributionDonut) ──────────────────────────────────────────
  const pieData: DistributionSlice[] = zoneStats.slice(0, 10).map((z, idx) => ({
    id: z.zone,
    label: z.label,
    value: z.count,
    percent: totalComplaints > 0 ? Math.round((z.count / totalComplaints) * 100) : null,
    color: getPaletteColor(idx),
  }));

  // ── Sorted rows ─────────────────────────────────────────────────────────────
  const sortedDocs = useMemo(() => {
    if (!docs) return [];
    return [...docs].sort((a, b) => {
      const diff = dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
      return sortDir === 'desc' ? -diff : diff;
    });
  }, [docs, sortDir]);

  const totalDays = docs?.length ?? 0;
  const avgPerDay =
    totalDays > 0 ? Math.round((totalComplaints / totalDays) * 10) / 10 : 0;
  const highIntensityCount = allComplaints.filter((c) => c.intensity >= 4).length;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.wrapper}>
      {/* Header + filtri */}
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <span className={styles.title}>Storico Fastidi</span>
          {!isLoading && !isError && (
            <span className={styles.badge}>{totalDays} giorni · {totalComplaints} segnalazioni</span>
          )}
        </div>
        <div className={styles.filters}>
          <label className={styles.filterLabel}>Da</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
            className={styles.dateInput}
          />
          <label className={styles.filterLabel}>A</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            max={today}
            onChange={(e) => setDateTo(e.target.value)}
            className={styles.dateInput}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.stateWrap}>
          <LeopartLoader size="sm" />
        </div>
      ) : isError ? (
        <div className={styles.stateWrap}>
          <ChartErrorState onRetry={() => refetch()} />
        </div>
      ) : totalComplaints === 0 ? (
        <div className={styles.emptyState}>
          Nessuna segnalazione nel periodo selezionato
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className={styles.statsRow}>
            <KpiCard label="Giorni con segnalazioni" value={String(totalDays)} />
            <KpiCard label="Fastidi totali" value={String(totalComplaints)} />
            <KpiCard label="Media per giorno" value={String(avgPerDay)} />
            <KpiCard
              label="Alta intensità (≥4)"
              value={String(highIntensityCount)}
              sub={`${Math.round((highIntensityCount / totalComplaints) * 100)}%`}
              tone={highIntensityCount > 0 ? 'danger' : 'neutral'}
            />
          </div>

          {/* Charts + type stats */}
          <div className={styles.chartsRow}>
            {/* Donut zone */}
            <div className={styles.chartCard}>
              <div className={styles.cardTitle}>Distribuzione per zona</div>
              <DistributionDonut data={pieData} height={260} />
            </div>

            {/* Type stats table */}
            <div className={styles.chartCard}>
              <div className={styles.cardTitle}>Tipologie segnalate</div>
              <table className={styles.miniTable}>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Segnalazioni</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {typeStats.map((t) => (
                    <tr key={t.type}>
                      <td>{t.label}</td>
                      <td className={styles.countCell}>{t.count}</td>
                      <td className={styles.pctCell}>
                        {Math.round((t.count / totalComplaints) * 100)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className={styles.cardSubTitle}>Zone più colpite</div>
              <table className={styles.miniTable}>
                <thead>
                  <tr>
                    <th>Zona</th>
                    <th>Volte</th>
                    <th>Int. media</th>
                  </tr>
                </thead>
                <tbody>
                  {zoneStats.slice(0, 6).map((z) => (
                    <tr key={z.zone}>
                      <td>{z.label}</td>
                      <td className={styles.countCell}>{z.count}</td>
                      <td className={styles.intensityCell}>
                        <span
                          className={styles.intensityBadge}
                          style={{ background: getIntensityColor(Math.round(z.avgIntensity)) }}
                        >
                          {z.avgIntensity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Storico tabella */}
          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <div className={styles.cardTitle}>Storico segnalazioni</div>
              <button
                className={styles.sortBtn}
                onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
              >
                Data {sortDir === 'desc' ? '↓' : '↑'}
              </button>
            </div>
            <div className={styles.tableWrapper}>
              <table className={styles.historyTable}>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Zona</th>
                    <th>Tipo</th>
                    <th>Intensità</th>
                    <th>Impatto</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocs.map((doc) =>
                    doc.complaints.map((c, idx) => (
                      <tr key={`${doc._id}-${idx}`}>
                        {idx === 0 && (
                          <td rowSpan={doc.complaints.length} className={styles.dateCell}>
                            {dayjs(doc.date).format('DD/MM/YYYY')}
                          </td>
                        )}
                        <td>{ZONE_LABELS[c.zone] ?? c.zone}</td>
                        <td>{TYPE_LABELS[c.type] ?? c.type}</td>
                        <td>
                          <span
                            className={styles.intensityBadge}
                            style={{ background: getIntensityColor(c.intensity) }}
                          >
                            {c.intensity} — {getIntensityLabel(c.intensity)}
                          </span>
                        </td>
                        <td
                          className={styles.impactCell}
                          style={{ color: getImpactColor(c.impact) }}
                        >
                          {IMPACT_LABELS[c.impact] ?? c.impact}
                        </td>
                        <td className={styles.noteCell}>{c.note ?? '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AthleteBodyMapHistoryView;
